<Modal
    id="itsl-move-to-modal-id"
    bind:enabled={showMoveToModal}
    titleText={config.moveResourceToAnotherPlanText}
    confirmText={i18n.move}
    cancelText={i18n.cancel}
    on:close={moveResourceModalClicked}
>
    <div slot="body" class="itsl-move-to-modal__body">
        {#if isDateVisible()}
            <Select
                label={i18n.filterPlans}
                labelPosition="left"
                id="FilterPlansResourceMoveId"
                bind:value={selectedFilter}
                on:change={changeFilterValue}
            >
                <option value={moveToAnotherPlanFilter.All}>{i18n.allPlans}</option>
                <option value={moveToAnotherPlanFilter.Current}
                    >{i18n.moveToCurrentAndUpcomingPlans}</option
                >
                <option value={moveToAnotherPlanFilter.Past}>{i18n.moveToPastPlans}</option>
                <option value={moveToAnotherPlanFilter.NoDates}>{i18n.moveToNoDatePlans}</option>
            </Select>
        {/if}
        {#if plans.length > 0}
            <ul class="itsl-move-to-modal-plan-card__list">
                {#each plans as plan (plan.id)}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                    <li class="itsl-move-to-modal-plan-card__item" on:click={cardClicked(plan.id)}>
                        <div class="itsl-move-to-modal-plan-card__selector">
                            <RadioButton
                                id="radio-button-{plan.id}"
                                label={plan.title}
                                name="PlanRadioGroup"
                                value={plan.id}
                                small="true"
                                bind:selected
                            />
                        </div>
                        <ul class="itsl-move-to-modal-plan-card__innerlist">
                            <PlanCard
                                {plan}
                                {i18n}
                                disabled={false}
                                hasAccessToPlan={false}
                                canEdit={false}
                                topicName={plan.topicName}
                                showTopicAsLink={false}
                                locale={config.locale}
                                backExtraParams={location}
                                isLearner={plan.isLearner}
                                columnVisibilities={plan.columnVisibilities}
                                useGenericResourceColumn={plan.useGenericResourceColumn}
                            />
                        </ul>
                    </li>
                {/each}
            </ul>
        {:else}
            <EmptyState
                iconName="plans"
                title={i18n.emptyState_Tab_Title_Teacher}
                description={emptyStateDescription}
                showButton={false}
            />
        {/if}
    </div>
</Modal>

<script>
    import { createEventDispatcher } from "svelte";

    import Select from "@itslearning/prometheus/assets/inputs/Select/v1/Select.svelte";
    import RadioButton from "@itslearning/prometheus/assets/inputs/RadioButton/v1/RadioButton.svelte";
    import Modal from "@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte";
    import EmptyState from "@itslearning/prometheus/assets/nodes/EmptyState/v1/EmptyState.svelte";
    import PlanCard from "../../components/PlanCard/PlanCard.svelte";

    import {
        topicCurrentPlansData,
        topicPastPlansData,
        topicWithoutDatePlansData,
        topicAllPlansData
    } from "../../logic/planPage/stores";

    import {
        loadTopicCurrentPlans,
        loadTopicWithoutDatePlans,
        loadTopicPastPlans,
        loadTopicAllPlans
    } from "../../helpers/loadPlansHelper";

    import { moveElementToPlan, getPlan } from "../../api";
    import { isDateVisible } from "../../helpers/visibilityHelper";
    import { noTopicTopicId } from "../../constants";

    export let loadMethod = null;
    export let location = {};
    export let showMoveToModal;
    export let elementMoveTo;
    export let sourcePlanId;
    export let sourcePlanTopicId;
    export let i18n;
    export let config;
    export let selected;

    const moveToAnotherPlanFilter = {
        All: "0",
        Current: "1",
        Past: "2",
        NoDates: "3"
    };

    const dispatch = createEventDispatcher();

    let selectedFilter;
    let emptyStateDescription;

    $: plans = [];

    $: if (showMoveToModal) {
        initState();
    }

    async function initState() {
        const selectedSourcePlan = await getPlan(sourcePlanId);

        sourcePlanTopicId = selectedSourcePlan.topic ? selectedSourcePlan.topic.id : noTopicTopicId;
        await loadAllPlansForTopic();

        if (plans.length > 0) {
            selected = plans[0].id;
        }
    }

    const changeFilterValue = async event => {
        const selectedFilterId = event.detail;

        if (selectedFilterId === moveToAnotherPlanFilter.All) {
            await loadAllPlansForTopic();
        } else if (selectedFilterId === moveToAnotherPlanFilter.Current) {
            await loadCurrentPlansForTopic();
        } else if (selectedFilterId === moveToAnotherPlanFilter.Past) {
            await loadPastPlansForTopic();
        } else if (selectedFilterId === moveToAnotherPlanFilter.NoDates) {
            await loadWithoutDatePlansForTopic();
        }

        if (plans.length > 0) {
            selected = plans[0].id;
        }
    };

    async function loadAllPlansForTopic() {
        selectedFilter = moveToAnotherPlanFilter.All;
        loadMethod = await loadTopicAllPlans(topicAllPlansData, sourcePlanTopicId, false);
        plans = [...$topicAllPlansData.plans].filter(p => p.id !== sourcePlanId);
        emptyStateDescription = "";
    }

    async function loadCurrentPlansForTopic() {
        selectedFilter = moveToAnotherPlanFilter.Current;
        loadMethod = await loadTopicCurrentPlans(topicCurrentPlansData, sourcePlanTopicId, false);
        plans = [...$topicCurrentPlansData.plans].filter(p => p.id !== sourcePlanId);
        emptyStateDescription = i18n.emptyState_CurrentTab_Description;
    }

    async function loadPastPlansForTopic() {
        loadMethod = await loadTopicPastPlans(topicPastPlansData, sourcePlanTopicId, false);
        plans = [...$topicPastPlansData.plans].filter(p => p.id !== sourcePlanId);
        emptyStateDescription = i18n.emptyState_PastTab_Description;
    }

    async function loadWithoutDatePlansForTopic() {
        loadMethod = await loadTopicWithoutDatePlans(
            topicWithoutDatePlansData,
            sourcePlanTopicId,
            false
        );
        plans = [...$topicWithoutDatePlansData.plans].filter(p => p.id !== sourcePlanId);
        emptyStateDescription = i18n.emptyState_WithoutDateTab_Description;
    }

    async function moveResourceModalClicked(event) {
        if (event.detail.confirmed === true) {
            let columnId;

            if (config.useGenericResourceColumn) {
                columnId = config.elementColumnId;
            } else {
                if (elementMoveTo.isResource) {
                    columnId = config.resourceColumnId;
                } else {
                    columnId = config.activityColumnId;
                }
            }

            const result = await moveElementToPlan(
                elementMoveTo.id,
                selected,
                sourcePlanId,
                columnId
            );

            if (result.Success) {
                dispatch("elementMoveTo", {
                    element: elementMoveTo,
                    targetPlanId: selected,
                    sourcePlanId: sourcePlanId
                });
            }
        }

        showMoveToModal = false;
    }

    function cardClicked(planId) {
        selected = planId;

        const focusCard = document.querySelector(`#itsl-plan-card-${planId}`);

        if (focusCard) {
            focusCard.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }
</script>
